/**
 * FP3-046 — Frame Practice Terms of Use acceptance gate.
 *
 * Mounted at /terms. Reachable by any AUTHENTICATED user. Renders
 * the current platform terms + a single acceptance checkbox. The
 * Accept button stays disabled until the checkbox is checked.
 *
 * If the authenticated user has already accepted the current
 * version, this component redirects to the role-aware landing page.
 * If the user is NOT authenticated, it redirects to /login.
 */
import { useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

export default function TermsGate() {
  const { user, loading: authLoading, logout, refreshTermsStatus } = useAuth();
  const navigate = useNavigate();

  const [terms, setTerms] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) {
      setLoading(false);
      return;
    }
    setLoading(true);
    api.getTerms()
      .then((r) => {
        if (cancelled) return;
        setTerms(r.data);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e?.response?.data?.detail || e.message || "Could not load terms.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [user, authLoading]);

  // Not authenticated → punt to login. The /login flow brings the
  // user back into the gate once a session exists.
  if (!authLoading && !user) {
    return <Navigate to="/login" replace />;
  }

  // Already accepted current version → bounce to the role landing.
  if (terms && terms.accepted) {
    return <Navigate to="/" replace />;
  }

  const submit = async () => {
    if (!checked || !terms?.version) return;
    setSubmitting(true);
    try {
      await api.acceptTerms({
        terms_version: terms.version,
        confirmation: true,
      });
      // Re-pull /auth/me so the route guard learns about the new state.
      await refreshTermsStatus?.();
      toast.success("Thanks — you can continue to Frame Practice.");
      navigate("/", { replace: true });
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Could not save acceptance.";
      toast.error(detail);
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = async () => {
    try { await logout(); } catch { /* noop */ }
    navigate("/login", { replace: true });
  };

  return (
    <div
      data-testid="terms-page"
      className="min-h-screen w-full bg-[#FBF8F3] flex items-center justify-center px-4 py-10"
    >
      <div className="w-full max-w-3xl bg-white border border-[#E5E3DB] rounded-2xl shadow-sm p-8 md:p-10">
        {/* Header */}
        <div className="flex items-center justify-between gap-4 flex-wrap mb-6">
          <div className="min-w-0">
            <p className="text-[10.5px] uppercase tracking-[0.18em] text-[#9B9890] font-semibold">
              FrameEd · Frame Practice
            </p>
            <h1
              data-testid="terms-title"
              className="mt-1 text-[24px] md:text-[28px] font-semibold text-[#1F2937] heading-font leading-tight"
            >
              {terms?.title || "Frame Practice Terms of Use"}
            </h1>
            <p className="mt-1 text-[12px] text-[#6B7280]">
              Please review and accept these terms before using Frame Practice.
            </p>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            data-testid="terms-logout"
            className="text-[11px] text-[#6B7280] underline hover:text-[#1F2937]"
          >
            Log out
          </button>
        </div>

        {/* Intro */}
        <div className="mb-4 p-3 rounded-lg bg-[#FDF8F3] border border-[#F2DDB9] text-[12px] text-[#5A4A1A] leading-relaxed">
          {terms?.intro || (
            "To continue using Frame Practice, please review and accept the terms below."
          )}
        </div>

        {/* Loading / error */}
        {loading && (
          <p className="text-[12px] text-[#6B7280]" data-testid="terms-loading">
            Loading the latest terms…
          </p>
        )}
        {error && (
          <p
            className="text-[12px] text-[#A8453A] bg-[#FBEEEE] border border-[#F4D1D1] rounded-lg p-3"
            data-testid="terms-error"
          >
            {error}
          </p>
        )}

        {/* Agreement body — scrollable */}
        {terms && (
          <div
            data-testid="terms-body"
            className="max-h-[440px] overflow-y-auto pr-2 border border-[#E5E3DB] rounded-lg p-5 space-y-5 bg-[#FCFAF6]"
          >
            {(terms.sections || []).map((sec) => (
              <section key={sec.heading} data-testid={`terms-section-${slug(sec.heading)}`}>
                <h2 className="text-[13px] font-semibold text-[#1F2937] heading-font mb-1.5">
                  {sec.heading}
                </h2>
                {sec.paragraphs.map((p, i) => (
                  <p key={i} className="text-[12px] text-[#374151] leading-relaxed mb-1.5">
                    {p}
                  </p>
                ))}
              </section>
            ))}
          </div>
        )}

        {/* Footer note */}
        {terms?.footer_note && (
          <p className="mt-3 text-[10.5px] text-[#9B9890] italic">
            {terms.footer_note}
          </p>
        )}

        {/* Checkbox */}
        {terms && (
          <label
            data-testid="terms-checkbox-label"
            className="mt-5 flex items-start gap-3 cursor-pointer select-none"
          >
            <input
              type="checkbox"
              data-testid="terms-checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-[#d4d2cc] focus:ring-[#9C6B2E]"
            />
            <span className="text-[12.5px] text-[#1F2937] leading-relaxed">
              {terms.checkbox_label}
            </span>
          </label>
        )}

        {/* Version + Accept */}
        {terms && (
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <p
              className="text-[10.5px] text-[#9B9890]"
              data-testid="terms-version-label"
            >
              Version: {terms.version} · Effective {terms.effective_date}
            </p>
            <button
              type="button"
              onClick={submit}
              disabled={!checked || submitting}
              data-testid="terms-accept"
              className="px-5 py-2.5 rounded-lg bg-[#9C6B2E] text-white text-[13px] font-semibold hover:bg-[#7A521F] disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              {submitting ? "Saving…" : "Accept and Continue"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function slug(s) {
  return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}
